"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { PlayIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { focusRing } from "@/components/landing/cta-styles";

import { clamp, easeInOut, prefersReducedMotion, tryPlay, useInView } from "./motion";

/** WebM (AV1 + Opus) first — roughly half the bytes — with the H.264 MP4 as
 *  the fallback for browsers that can't play it (older Safari). The browser
 *  picks the first `<source>` it can decode. */
const FILM_WEBM = "/landing/smb/film/busy-ones.webm";
const FILM_MP4 = "/landing/smb/film/busy-ones.mp4";
const FILM_POSTER = "/landing/smb/film/busy-ones-poster.jpg";

/**
 * "Here's to the busy ones" — self-hosted, so no third-party player loads and
 * no visitor IP leaves for a video CDN. Nothing is fetched until the frame
 * comes within 700px of the viewport; then it plays muted on a loop while on
 * screen (under reduced motion it waits for the button). "Play with sound"
 * restarts it from the top with sound and the native controls, and when it
 * ends it goes back to the silent loop. The frame grows from 88% to full
 * size as it scrolls up into view.
 *
 * The scroll scale and the progress bar are written to the DOM directly: both
 * change many times a second and nothing else renders from them.
 */
export function FilmPlayer() {
  const { t } = useLingui();
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const near = useInView(frameRef, 0, "700px 0px 700px 0px", true);
  const visible = useInView(frameRef, 0.4);
  const [withSound, setWithSound] = useState(false);

  // Visibility drives the silent loop; a sound playback is left alone.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (visible) {
      if (!prefersReducedMotion() || withSound) tryPlay(video);
    } else {
      video.pause();
    }
  }, [visible, withSound]);

  // Under reduced motion the listener never attaches and the frame's
  // `motion-reduce:transform-none!` class wins over the inline scale.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || prefersReducedMotion()) return;
    const update = () => {
      const r = frame.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = clamp((vh - r.top) / (vh * 0.85), 0, 1);
      frame.style.transform = `scale(${(0.88 + 0.12 * easeInOut(p)).toFixed(4)})`;
    };
    const first = requestAnimationFrame(update);
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(first);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const playWithSound = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setWithSound(true);
    video.muted = false;
    video.currentTime = 0;
    tryPlay(video);
  }, []);

  const onEnded = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setWithSound(false);
    video.muted = true;
    video.currentTime = 0;
    if (visible && !prefersReducedMotion()) tryPlay(video);
  }, [visible]);

  return (
    <div
      ref={frameRef}
      className="relative aspect-video overflow-hidden rounded-[clamp(10px,1.4vw,22px)] bg-black shadow-[0_60px_120px_-40px_rgba(0,0,0,0.7),0_0_0_1px_rgba(250,250,248,0.12)] will-change-transform motion-reduce:transform-none!"
      style={{ transform: "scale(0.88)", transformOrigin: "50% 30%" }}
    >
      <video
        ref={videoRef}
        className="size-full object-cover"
        poster={FILM_POSTER}
        muted={!withSound}
        loop={!withSound}
        controls={withSound}
        playsInline
        preload={near ? "metadata" : "none"}
        aria-label={t`Here's to the busy ones, a 38 second film`}
        onEnded={onEnded}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (barRef.current && v.duration) barRef.current.style.width = `${((v.currentTime / v.duration) * 100).toFixed(2)}%`;
        }}
      >
        <source src={FILM_WEBM} type="video/webm" />
        <source src={FILM_MP4} type="video/mp4" />
      </video>
      <button
        type="button"
        onClick={playWithSound}
        className={`absolute bottom-[clamp(14px,2vw,28px)] left-[clamp(14px,2vw,28px)] inline-flex h-[46px] items-center gap-3 rounded-full bg-[#FAFAF8]/95 py-0 pl-2 pr-[18px] text-sm font-semibold text-[#0A1628] shadow-[0_12px_30px_-10px_rgba(0,0,0,0.5)] transition-[transform,opacity] duration-300 hover:scale-[1.04] motion-reduce:transition-none sm:h-[54px] sm:pr-6 sm:text-base ${focusRing} ${
          withSound ? "pointer-events-none opacity-0" : ""
        }`}
      >
        <i className="grid size-8 place-items-center rounded-full bg-signal text-[#FAFAF8] sm:size-10">
          <PlayIcon className="ml-0.5 size-3.5" fill="currentColor" strokeWidth={0} aria-hidden="true" />
        </i>
        <Trans>Play with sound</Trans>
      </button>
      <div ref={barRef} aria-hidden="true" className="absolute bottom-0 left-0 h-[3px] w-0 bg-signal" />
    </div>
  );
}
